/**
 * ApplicationsListPage
 * Lists all credit applications with search, filters, and click-to-detail navigation
 * Route: /credit/origination (replaces ComingSoon)
 *
 * Vista agrupada por cliente: cada cliente aparece una vez con su solicitud
 * mas reciente y un badge "x N" cuando hay historicas. Al expandir el grupo
 * se listan las anteriores como sub-filas indentadas. Por default se ocultan
 * las solicitudes en estados terminales (EXPIRED, CANCELLED) — se pueden
 * incluir con el pill "Ver N terminadas". DISBURSED se muestra siempre
 * porque es resultado positivo.
 */

import { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { RefreshCw, AlertTriangle, ChevronRight, ChevronDown } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { Skeleton } from '@/components/ui/Skeleton';
import { Badge } from '@/components/ui/Badge';
import { CreditLevelBadge } from '@/features/credit/components/CreditLevelBadge';
import { DataTable } from '@/features/credit/components/DataTable';
import { ApplicationDetailModal } from '@/features/credit/components/ApplicationDetailModal';
import { useBackofficeApplications } from '@/features/credit/hooks';
import { formatCurrency } from '@/utils/formatters';
import { ROUTES } from '@/constants';

import { CreditLevel } from '@/types/credit.types';
import type { BackofficeApplicationsListItem } from '@/types/backoffice-crud.types';
import { APPLICATION_STATUSES, CREDIT_LEVELS } from '@/types/backoffice-crud.types';

// ─── Constants ────────────────────────────────────────────────────────────────

const BREADCRUMBS = [
  { label: 'Inicio', path: ROUTES.DASHBOARD },
  { label: 'Originacion', path: ROUTES.PIPELINE_MATRIX },
  { label: 'Solicitudes', path: ROUTES.APPLICATIONS_LIST },
];

type BadgeVariant = 'default' | 'success' | 'warning' | 'danger' | 'info';

function getStatusVariant(status: string): BadgeVariant {
  switch (status) {
    case 'SIGNED':
    case 'DISBURSED':
      return 'success';
    case 'PENDING_SIGNATURE':
    case 'CREATED':
      return 'warning';
    case 'EXPIRED':
    case 'CANCELLED':
      return 'danger';
    default:
      return 'default';
  }
}

function formatDateShort(dateStr: string): string {
  if (!dateStr) return 'Pendiente';
  const date = new Date(dateStr);
  return new Intl.DateTimeFormat('es-MX', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date);
}

// ─── Row Type ────────────────────────────────────────────────────────────────

/**
 * Row del listado agrupado. Un cliente con N solicitudes produce 1 fila
 * primaria (isPrimary=true, groupCount=N, con la solicitud MAS RECIENTE) +
 * N-1 sub-filas (isSubrow=true) que solo se renderizan cuando el grupo esta
 * expandido.
 */
type ApplicationRow = BackofficeApplicationsListItem & {
  _rowId: string;
  _isPrimary: boolean;
  _isSubrow: boolean;
  _groupCount: number;
  _directoryId: string;
};

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function ListSkeleton() {
  return (
    <div className="space-y-4">
      <div className="flex gap-4">
        <Skeleton variant="rectangular" width="320px" height={40} />
        <Skeleton variant="rectangular" width="160px" height={40} />
      </div>
      <div className="space-y-3">
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={`row-skeleton-${i.toString()}`} variant="rectangular" height={44} />
        ))}
      </div>
    </div>
  );
}

// ─── Error State ──────────────────────────────────────────────────────────────

function ListError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-border bg-card px-6 py-16">
      <AlertTriangle size={48} className="mb-4 text-muted-foreground" />
      <h3 className="text-lg font-semibold text-foreground">
        Error al cargar las solicitudes
      </h3>
      <p className="mt-1 text-sm text-muted-foreground">
        Hubo un error al obtener los datos. Intenta de nuevo.
      </p>
      <Button className="mt-4" onClick={onRetry}>
        <RefreshCw size={16} />
        Reintentar
      </Button>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export function ApplicationsListPage() {
  const { applications, isLoading, isError, refetch } = useBackofficeApplications();
  const [searchParams] = useSearchParams();

  const [statusFilter, setStatusFilter] = useState('');
  const [levelFilter, setLevelFilter] = useState(searchParams.get('level') ?? '');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [selectedApplication, setSelectedApplication] = useState<{
    applicationId: string;
    directoryId: string;
  } | null>(null);

  // Filtrar por status + nivel. NO ocultamos terminales por default: la
  // agrupacion por cliente + caret ▶ ya expone el historico. Si un cliente
  // solo tiene solicitudes terminales, aparece con su mas reciente como primary.
  const filteredApplications = useMemo(() => {
    let result = applications;
    if (statusFilter) {
      result = result.filter((a) => a.status === statusFilter);
    }
    if (levelFilter) {
      result = result.filter((a) => a.credit_level === levelFilter);
    }
    return result;
  }, [applications, statusFilter, levelFilter]);

  // Paso 2: agrupar por directory_id y armar las filas primarias (+ sub-filas
  // cuando el grupo esta expandido). Orden dentro del grupo: created_at DESC.
  // Orden de los grupos: por la fecha de la solicitud mas reciente, DESC.
  const rows = useMemo<ApplicationRow[]>(() => {
    const groups = new Map<string, BackofficeApplicationsListItem[]>();
    for (const a of filteredApplications) {
      const key = a.directory_id;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(a);
    }

    const primaries: ApplicationRow[] = [];
    for (const [directoryId, list] of groups) {
      list.sort((a, b) => (b.created_at ?? '').localeCompare(a.created_at ?? ''));
      const [head, ...rest] = list;
      if (!head) continue;
      const isExpanded = expandedGroups.has(directoryId);
      primaries.push({
        ...head,
        _rowId: `${directoryId}:head`,
        _isPrimary: true,
        _isSubrow: false,
        _groupCount: list.length,
        _directoryId: directoryId,
      });
      if (isExpanded) {
        for (const sub of rest) {
          primaries.push({
            ...sub,
            _rowId: `${directoryId}:${sub.application_id}`,
            _isPrimary: false,
            _isSubrow: true,
            _groupCount: list.length,
            _directoryId: directoryId,
          });
        }
      }
    }

    // Ordenar los grupos por la fecha de su primaria (created_at) desc,
    // preservando primaria + subrows juntas.
    const blocks = new Map<string, ApplicationRow[]>();
    for (const r of primaries) {
      const key = r._directoryId;
      if (!blocks.has(key)) blocks.set(key, []);
      blocks.get(key)!.push(r);
    }
    const sortedKeys = Array.from(blocks.keys()).sort((a, b) => {
      const aHead = blocks.get(a)!.find((x) => x._isPrimary);
      const bHead = blocks.get(b)!.find((x) => x._isPrimary);
      return (bHead?.created_at ?? '').localeCompare(aHead?.created_at ?? '');
    });
    return sortedKeys.flatMap((k) => blocks.get(k)!);
  }, [filteredApplications, expandedGroups]);

  const toggleGroup = (directoryId: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(directoryId)) next.delete(directoryId);
      else next.add(directoryId);
      return next;
    });
  };

  const columns = useMemo<ColumnDef<ApplicationRow, unknown>[]>(() => [
    {
      id: 'expand',
      header: '',
      cell: ({ row }) => {
        const r = row.original;
        if (!r._isPrimary || r._groupCount <= 1) {
          return <span className="inline-block w-4" aria-hidden />;
        }
        const isOpen = expandedGroups.has(r._directoryId);
        return (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              toggleGroup(r._directoryId);
            }}
            className="inline-flex h-5 w-5 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label={isOpen ? 'Colapsar historial' : 'Expandir historial'}
            aria-expanded={isOpen}
          >
            {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>
        );
      },
    },
    {
      accessorKey: 'created_at',
      header: 'Creada',
      cell: ({ row }) => (
        <span className={row.original._isSubrow ? 'text-muted-foreground' : ''}>
          {formatDateShort(row.original.created_at)}
        </span>
      ),
    },
    {
      accessorKey: 'signed_at',
      header: 'Firmada',
      cell: ({ row }) => (
        <span className={row.original._isSubrow ? 'text-muted-foreground' : ''}>
          {formatDateShort(row.original.signed_at)}
        </span>
      ),
    },
    {
      accessorKey: 'full_name',
      header: 'Nombre',
      cell: ({ row }) => {
        const r = row.original;
        if (r._isSubrow) {
          return (
            <span className="pl-6 text-xs text-muted-foreground">Solicitud previa</span>
          );
        }
        const name = r.full_name;
        const display = name && name !== 'N/A' ? name : '—';
        return (
          <span className="inline-flex items-center gap-2">
            <span className="font-medium">{display}</span>
            {r._groupCount > 1 && (
              <span
                className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold text-muted-foreground"
                title={`${r._groupCount} solicitudes historicas del cliente`}
              >
                × {r._groupCount}
              </span>
            )}
          </span>
        );
      },
    },
    {
      accessorKey: 'phone_number',
      header: 'Telefono',
      cell: ({ row }) => (
        row.original._isSubrow ? (
          <span className="text-muted-foreground" aria-hidden>—</span>
        ) : (
          <span className="tabular-nums">{row.original.phone_number}</span>
        )
      ),
    },
    {
      accessorKey: 'credit_level',
      header: 'Nivel',
      cell: ({ getValue }) => {
        const level = getValue<string | undefined>();
        if (!level) return '-';
        const isValid = Object.values(CreditLevel).includes(level as CreditLevel);
        return isValid ? <CreditLevelBadge level={level as CreditLevel} size="sm" /> : level;
      },
    },
    {
      accessorKey: 'amount_mxn',
      header: 'Monto',
      cell: ({ getValue }) => (
        <span className="tabular-nums">
          {formatCurrency(getValue<number>(), 'MXN', 'es-MX')}
        </span>
      ),
    },
    {
      accessorKey: 'term_weeks',
      header: 'Plazo',
      cell: ({ getValue }) => `${getValue<number>()} semanas`,
    },
    {
      accessorKey: 'annual_rate',
      header: 'Tasa Anual',
      cell: ({ getValue }) => {
        const rate = getValue<number | undefined>();
        return rate != null ? `${(rate * 100).toFixed(1)}%` : '-';
      },
    },
    {
      accessorKey: 'weekly_payment_mxn',
      header: 'Pago Semanal',
      cell: ({ getValue }) => {
        const payment = getValue<number | undefined>();
        return payment != null ? (
          <span className="tabular-nums">{formatCurrency(payment, 'MXN', 'es-MX')}</span>
        ) : '-';
      },
    },
    {
      accessorKey: 'status',
      header: 'Estado',
      cell: ({ getValue }) => {
        const status = getValue<string>();
        return <Badge variant={getStatusVariant(status)}>{status}</Badge>;
      },
    },
  ], [expandedGroups]);

  const statusOptions = [
    { value: '', label: 'Todos los estados' },
    ...APPLICATION_STATUSES.map((s) => ({ value: s, label: s })),
  ];

  const handleRowClick = (row: ApplicationRow) => {
    setSelectedApplication({
      applicationId: row.application_id,
      directoryId: row.directory_id,
    });
  };

  if (isLoading) {
    return (
      <div>
        <PageHeader
          title="Solicitudes de Credito" helpKey="applications-list"
          subtitle="Lista de solicitudes del portafolio"
          breadcrumbs={BREADCRUMBS}
        />
        <ListSkeleton />
      </div>
    );
  }

  if (isError) {
    return (
      <div>
        <PageHeader
          title="Solicitudes de Credito" helpKey="applications-list"
          subtitle="Lista de solicitudes del portafolio"
          breadcrumbs={BREADCRUMBS}
        />
        <ListError onRetry={() => refetch()} />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Solicitudes de Credito" helpKey="applications-list"
        subtitle="Lista de solicitudes del portafolio"
        breadcrumbs={BREADCRUMBS}
        actions={
          <div className="flex items-center gap-2">
            <Button onClick={() => refetch()}>
              <RefreshCw size={16} />
              Actualizar
            </Button>
          </div>
        }
      />

      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Select
          options={statusOptions}
          value={statusFilter}
          onChange={setStatusFilter}
          placeholder="Filtrar por estado"
        />
        <Select
          options={[{ value: '', label: 'Todos los niveles' }, ...CREDIT_LEVELS.map((l) => ({ value: l, label: l }))]}
          value={levelFilter}
          onChange={setLevelFilter}
          placeholder="Filtrar por nivel"
        />
      </div>

      <DataTable
        data={rows}
        columns={columns}
        searchPlaceholder="Buscar por telefono o ID de solicitud..."
        onRowClick={handleRowClick}
        emptyMessage="No se encontraron solicitudes"
        pageSize={10}
      />

      {selectedApplication && (
        <ApplicationDetailModal
          isOpen
          applicationId={selectedApplication.applicationId}
          directoryId={selectedApplication.directoryId}
          onClose={() => setSelectedApplication(null)}
        />
      )}
    </div>
  );
}
